import { useCallback, useEffect, useState } from 'react';

interface QueryState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

/**
 * Wrapper delgado sobre una llamada async a Supabase: expone
 * {data, error, loading} y una función `refetch`, sin traer una librería
 * de cache de datos — ver ARQUITECTURA_FRONTEND.md sección 3 para la
 * justificación y el criterio de cuándo migrar a TanStack Query.
 *
 * `deps` sigue la misma semántica que el array de dependencias de
 * useEffect: la consulta se vuelve a ejecutar cuando cambian.
 */
export function useSupabaseQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: { message: string } | null }>,
  deps: unknown[] = [],
) {
  const [state, setState] = useState<QueryState<T>>({ data: null, error: null, loading: true });

  const run = useCallback(() => {
    let activo = true;
    setState((s) => ({ ...s, loading: true }));

    queryFn().then(({ data, error }) => {
      if (!activo) return;
      setState({ data, error: error ? error.message : null, loading: false });
    });

    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => run(), [run]);

  return { ...state, refetch: run };
}
